import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PitchVideoPlayer from "./PitchVideoPlayer";
import { useTawkToSuppressWhile } from "../context/TawkToContext";
import { captureAnalyticsEvent } from "../lib/analytics";

const MAX_PITCH_RECORD_SECONDS = 5 * 60;
const PITCH_COUNTDOWN_MS = 500;
const SUPPORTS_RECORDER_PAUSE =
  typeof MediaRecorder !== "undefined" && typeof MediaRecorder.prototype.pause === "function";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function formatPitchTime(totalSeconds) {
  const safe = Math.max(0, totalSeconds);
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Shared pitch recording + preview modal used for hiring job-create pitch and applicant apply pitch.
 * Matches hiring layout/classes so video framing matches the job-create flow.
 */
export default function PitchRecordModal({
  open,
  onRequestClose,
  title,
  titleId = "pitch-record-modal-title",
  introParagraphs = [],
  limitLine,
  submitLabel,
  submittingLabel = "Submitting…",
  submitBusy = false,
  onSubmitPitch,
  pitchVideoAriaLabel = "Pitch preview",
  showMobilePitchLink = false,
  /** When provided, shows a "How \"apply\" works?" link that opens an explainer with this video. */
  applyHelpVideoSrc = "",
  /** Defaults to applicant green (P1). Hiring can pass `job-create-pitch-save-btn` to use P2. */
  submitButtonClassName = "apply-shine-submit-btn",
  /** Sync parent refs (e.g. hiring draft fingerprint) when recording changes. */
  onRecordingBlobChange,
  /**
   * When true, successful submit does not call `onRecordingBlobChange(null)` so the parent can keep bytes for a later
   * upload (e.g. hiring job create before a draft id exists).
   */
  retainParentRecordingBlobAfterSubmit = false,
  /** When true and an in-modal preview exists, cancel/close asks before discarding (hiring flow). */
  warnDiscardUnsavedPreview = false,
  discardUnsavedPreviewMessage = "You haven't saved this recording yet — your current pitch is still the one below.",
  /** Passed to PostHog as `context` on pitch_recording_stopped (e.g. application, hiring_job_create). */
  analyticsContext = "",
  cancelLabel = "Cancel",
}) {
  useTawkToSuppressWhile(open);

  const [showMobilePitchInfo, setShowMobilePitchInfo] = useState(false);
  const [showApplyHelpInfo, setShowApplyHelpInfo] = useState(false);
  const [showDiscardPreviewConfirm, setShowDiscardPreviewConfirm] = useState(false);
  const [pitchCameraError, setPitchCameraError] = useState(null);
  const [pitchRecording, setPitchRecording] = useState(false);
  const [pitchRecordingPaused, setPitchRecordingPaused] = useState(false);
  const [pitchHasPlayback, setPitchHasPlayback] = useState(false);
  const [pitchModalPlayerSrc, setPitchModalPlayerSrc] = useState("");
  const [pitchRecordedDurationSec, setPitchRecordedDurationSec] = useState(0);
  const [pitchCameraReady, setPitchCameraReady] = useState(false);
  const [pitchCountdownNumber, setPitchCountdownNumber] = useState(null);
  const [pitchRecordingRemainingSec, setPitchRecordingRemainingSec] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pitchVideoRef = useRef(null);
  const pitchStreamRef = useRef(null);
  const pitchRecorderRef = useRef(null);
  const pitchChunksRef = useRef([]);
  const pitchPlaybackUrlRef = useRef(null);
  const pitchCancelledRef = useRef(false);
  const pitchRecordingTimerRef = useRef(null);
  const pitchModalGenRef = useRef(0);
  const pitchPendingBlobRef = useRef(null);
  const pitchPendingMimeRef = useRef("");
  const pitchElapsedSecRef = useRef(0);
  const pitchTimerPausedRef = useRef(false);
  const pitchStopWaitRef = useRef(null);

  const clearPitchRecordingTimer = useCallback(() => {
    if (pitchRecordingTimerRef.current) {
      clearInterval(pitchRecordingTimerRef.current);
      pitchRecordingTimerRef.current = null;
    }
    setPitchRecordingRemainingSec(null);
    pitchElapsedSecRef.current = 0;
    pitchTimerPausedRef.current = false;
  }, []);

  const showPreviewFromPendingBlob = useCallback(() => {
    const blob = pitchPendingBlobRef.current;
    if (!(blob instanceof Blob) || blob.size === 0) return;
    const objectUrl = URL.createObjectURL(blob);
    if (pitchPlaybackUrlRef.current) URL.revokeObjectURL(pitchPlaybackUrlRef.current);
    pitchPlaybackUrlRef.current = objectUrl;
    setPitchModalPlayerSrc(objectUrl);
    setPitchHasPlayback(true);
    const live = pitchStreamRef.current;
    if (live) {
      live.getTracks().forEach((track) => {
        track.enabled = false;
      });
    }
    const video = pitchVideoRef.current;
    if (video) {
      video.pause();
      video.srcObject = null;
      video.removeAttribute("src");
      video.muted = true;
      video.currentTime = 0;
    }
  }, []);

  const runMediaCleanup = useCallback(
    (options = {}) => {
      const { skipParentBlobNotify = false } = options;
      pitchCancelledRef.current = true;
      clearPitchRecordingTimer();
      setPitchCountdownNumber(null);

      const recorder = pitchRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch {
          /* ignore */
        }
      }
      pitchRecorderRef.current = null;
      pitchChunksRef.current = [];
      if (pitchStopWaitRef.current) {
        pitchStopWaitRef.current.resolve(null);
        pitchStopWaitRef.current = null;
      }

      const stream = pitchStreamRef.current;
      if (stream) stream.getTracks().forEach((track) => track.stop());
      pitchStreamRef.current = null;

      const video = pitchVideoRef.current;
      if (video) {
        video.pause();
        video.srcObject = null;
        video.removeAttribute("src");
      }

      if (pitchPlaybackUrlRef.current) {
        URL.revokeObjectURL(pitchPlaybackUrlRef.current);
        pitchPlaybackUrlRef.current = null;
      }
      setPitchModalPlayerSrc("");
      setPitchRecordedDurationSec(0);
      setPitchCameraError(null);
      setPitchRecording(false);
      setPitchRecordingPaused(false);
      setPitchHasPlayback(false);
      setPitchCameraReady(false);
      pitchPendingBlobRef.current = null;
      pitchPendingMimeRef.current = "";
      setSubmitError("");
      setIsSubmitting(false);
      if (!skipParentBlobNotify) {
        onRecordingBlobChange?.(null, "", 0);
      }
    },
    [clearPitchRecordingTimer, onRecordingBlobChange]
  );

  const finalizeClose = useCallback(() => {
    runMediaCleanup();
    setShowMobilePitchInfo(false);
    setShowApplyHelpInfo(false);
    setShowDiscardPreviewConfirm(false);
    onRequestClose();
  }, [onRequestClose, runMediaCleanup]);

  const attemptClose = useCallback(() => {
    if (showDiscardPreviewConfirm) return;
    if (warnDiscardUnsavedPreview && pitchHasPlayback && !isSubmitting && !submitBusy) {
      setShowDiscardPreviewConfirm(true);
      return;
    }
    finalizeClose();
  }, [
    finalizeClose,
    isSubmitting,
    pitchHasPlayback,
    showDiscardPreviewConfirm,
    submitBusy,
    warnDiscardUnsavedPreview,
  ]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    pitchModalGenRef.current += 1;
    pitchCancelledRef.current = false;
    setPitchCameraReady(false);
    setPitchHasPlayback(false);
    setPitchRecording(false);
    setPitchRecordingPaused(false);
    setPitchCountdownNumber(null);
    setPitchRecordingRemainingSec(null);
    setSubmitError("");
    setIsSubmitting(false);
    if (pitchPlaybackUrlRef.current) {
      URL.revokeObjectURL(pitchPlaybackUrlRef.current);
      pitchPlaybackUrlRef.current = null;
    }
    setPitchModalPlayerSrc("");

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: { ideal: 1280, max: 1280 },
            height: { ideal: 720, max: 720 },
            aspectRatio: { ideal: 16 / 9 },
          },
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        pitchStreamRef.current = stream;
        const video = pitchVideoRef.current;
        if (video) {
          video.muted = true;
          video.playsInline = true;
          video.srcObject = stream;
          await video.play().catch(() => {});
        }
        setPitchCameraError(null);
        setPitchCameraReady(true);
      } catch {
        if (!cancelled) {
          setPitchCameraError(
            "We could not access your camera or microphone. Please allow access and try again."
          );
          setPitchCameraReady(false);
        }
      }
    };

    startCamera();
    return () => {
      cancelled = true;
      clearPitchRecordingTimer();
      setPitchCameraReady(false);
      const recorder = pitchRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch {
          /* ignore */
        }
      }
      pitchRecorderRef.current = null;
      const stream = pitchStreamRef.current;
      if (stream) stream.getTracks().forEach((track) => track.stop());
      pitchStreamRef.current = null;
      const video = pitchVideoRef.current;
      if (video) {
        video.pause();
        video.srcObject = null;
        video.removeAttribute("src");
      }
      if (pitchPlaybackUrlRef.current) {
        URL.revokeObjectURL(pitchPlaybackUrlRef.current);
        pitchPlaybackUrlRef.current = null;
      }
      setPitchModalPlayerSrc("");
    };
  }, [clearPitchRecordingTimer, onRecordingBlobChange, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (showDiscardPreviewConfirm) {
        setShowDiscardPreviewConfirm(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, showDiscardPreviewConfirm]);

  useLayoutEffect(() => {
    if (!open || pitchHasPlayback) return undefined;
    const stream = pitchStreamRef.current;
    const video = pitchVideoRef.current;
    if (!stream || !video) return undefined;
    stream.getTracks().forEach((track) => {
      track.enabled = true;
    });
    video.pause();
    video.removeAttribute("src");
    video.srcObject = stream;
    video.muted = true;
    void video.play().catch(() => {});
    setPitchCameraReady(true);
    return undefined;
  }, [open, pitchHasPlayback]);

  useEffect(
    () => () => {
      if (pitchPlaybackUrlRef.current) URL.revokeObjectURL(pitchPlaybackUrlRef.current);
    },
    []
  );

  const clearPreviewForRerecord = useCallback(() => {
    pitchPendingBlobRef.current = null;
    pitchPendingMimeRef.current = "";
    clearPitchRecordingTimer();
    setPitchCountdownNumber(null);
    setPitchRecordingPaused(false);
    if (pitchPlaybackUrlRef.current) {
      URL.revokeObjectURL(pitchPlaybackUrlRef.current);
      pitchPlaybackUrlRef.current = null;
    }
    setPitchModalPlayerSrc("");
    setPitchHasPlayback(false);
    setPitchRecordedDurationSec(0);
    onRecordingBlobChange?.(null, "", 0);
    const stream = pitchStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.enabled = true;
      });
    }
  }, [clearPitchRecordingTimer, onRecordingBlobChange]);

  const handleDeletePitchModalRecording = useCallback(() => {
    clearPreviewForRerecord();
  }, [clearPreviewForRerecord]);

  const beginPitchRecording = useCallback(() => {
    if (pitchCameraError || pitchCancelledRef.current) return;
    const stream = pitchStreamRef.current;
    if (!stream) return;
    pitchChunksRef.current = [];
    stream.getTracks().forEach((track) => {
      track.enabled = true;
    });

    const mimeCandidates = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
    const mimeType = mimeCandidates.find((type) => MediaRecorder.isTypeSupported(type)) || "";
    let recorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch {
      setPitchCameraError("Recording is not supported in this browser.");
      return;
    }
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) pitchChunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      pitchRecorderRef.current = null;
      setPitchRecording(false);
      setPitchRecordingPaused(false);
      pitchTimerPausedRef.current = false;
      const recordedSec = Math.max(1, pitchElapsedSecRef.current || 0);
      clearPitchRecordingTimer();
      const chunks = pitchChunksRef.current;
      pitchChunksRef.current = [];
      if (!chunks.length) {
        if (pitchStopWaitRef.current) {
          pitchStopWaitRef.current.resolve(null);
          pitchStopWaitRef.current = null;
        }
        return;
      }
      const blob = new Blob(chunks, { type: recorder.mimeType || "video/webm" });
      pitchPendingBlobRef.current = blob;
      pitchPendingMimeRef.current = recorder.mimeType || "video/webm";
      setPitchRecordedDurationSec(recordedSec);
      if (analyticsContext) {
        captureAnalyticsEvent("pitch_recording_stopped", {
          context: analyticsContext,
          duration_sec: recordedSec,
        });
      }
      onRecordingBlobChange?.(blob, pitchPendingMimeRef.current, recordedSec);

      const stopWait = pitchStopWaitRef.current;
      if (stopWait) {
        pitchStopWaitRef.current = null;
        stopWait.resolve({ blob, mime: pitchPendingMimeRef.current });
        return;
      }

      showPreviewFromPendingBlob();
    };
    pitchRecorderRef.current = recorder;
    try {
      recorder.start(250);
      setPitchRecording(true);
      setPitchRecordingPaused(false);
      pitchElapsedSecRef.current = 0;
      pitchTimerPausedRef.current = false;
      setPitchRecordingRemainingSec(MAX_PITCH_RECORD_SECONDS);
      pitchRecordingTimerRef.current = setInterval(() => {
        if (pitchCancelledRef.current || pitchTimerPausedRef.current) return;
        pitchElapsedSecRef.current += 1;
        const left = MAX_PITCH_RECORD_SECONDS - pitchElapsedSecRef.current;
        setPitchRecordingRemainingSec(left);
        if (pitchElapsedSecRef.current >= MAX_PITCH_RECORD_SECONDS) {
          if (pitchRecordingTimerRef.current) {
            clearInterval(pitchRecordingTimerRef.current);
            pitchRecordingTimerRef.current = null;
          }
          const rec = pitchRecorderRef.current;
          if (rec && rec.state === "recording") {
            try {
              rec.stop();
            } catch {
              /* ignore */
            }
          }
        }
      }, 1000);
    } catch {
      setPitchCameraError("Could not start recording.");
      pitchRecorderRef.current = null;
      clearPitchRecordingTimer();
    }
  }, [analyticsContext, clearPitchRecordingTimer, onRecordingBlobChange, pitchCameraError, showPreviewFromPendingBlob]);

  const handlePitchStopRecord = useCallback(() => {
    if (!pitchRecording) return;
    const recorder = pitchRecorderRef.current;
    if (!recorder || recorder.state === "inactive") return;
    try {
      if (recorder.state === "paused") recorder.resume();
      recorder.stop();
    } catch {
      setPitchCameraError("Could not stop recording — try again.");
    }
  }, [pitchRecording]);

  const handlePitchToggleRecord = useCallback(() => {
    if (pitchCameraError) return;
    if (pitchCountdownNumber !== null) return;
    const stream = pitchStreamRef.current;
    if (!stream) return;
    if (pitchRecording) {
      if (!SUPPORTS_RECORDER_PAUSE) return;
      const recorder = pitchRecorderRef.current;
      if (!recorder || recorder.state === "inactive") return;

      if (pitchRecordingPaused) {
        try {
          recorder.resume();
          pitchTimerPausedRef.current = false;
          setPitchRecordingPaused(false);
        } catch {
          setPitchCameraError("Could not resume recording.");
        }
      } else {
        try {
          recorder.pause();
          pitchTimerPausedRef.current = true;
          setPitchRecordingPaused(true);
        } catch {
          setPitchCameraError("Could not pause recording.");
        }
      }
      return;
    }
    if (pitchHasPlayback) {
      clearPreviewForRerecord();
    }
    const runCountdownThenRecord = async () => {
      const generation = pitchModalGenRef.current;
      if (pitchCancelledRef.current) return;
      setPitchCountdownNumber(3);
      await delay(PITCH_COUNTDOWN_MS);
      if (pitchModalGenRef.current !== generation || pitchCancelledRef.current) return;
      setPitchCountdownNumber(2);
      await delay(PITCH_COUNTDOWN_MS);
      if (pitchModalGenRef.current !== generation || pitchCancelledRef.current) return;
      setPitchCountdownNumber(1);
      await delay(PITCH_COUNTDOWN_MS);
      if (pitchModalGenRef.current !== generation || pitchCancelledRef.current) return;
      setPitchCountdownNumber(null);
      beginPitchRecording();
    };
    void runCountdownThenRecord();
  }, [
    beginPitchRecording,
    clearPreviewForRerecord,
    pitchCameraError,
    pitchCountdownNumber,
    pitchHasPlayback,
    pitchRecording,
    pitchRecordingPaused,
  ]);

  const handlePrimarySubmit = useCallback(async () => {
    if (isSubmitting || submitBusy) return;
    if (!pitchHasPlayback) return;

    setSubmitError("");
    setIsSubmitting(true);
    try {
      const blob = pitchPendingBlobRef.current;
      const mime = pitchPendingMimeRef.current || "video/webm";

      if (!(blob instanceof Blob) || blob.size === 0) {
        setSubmitError("Recording was lost — record again, then stop when you're happy with it.");
        return;
      }

      await onSubmitPitch({ blob, mime });
      runMediaCleanup({ skipParentBlobNotify: Boolean(retainParentRecordingBlobAfterSubmit) });
      onRequestClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Could not complete request.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    onRequestClose,
    onSubmitPitch,
    pitchHasPlayback,
    retainParentRecordingBlobAfterSubmit,
    runMediaCleanup,
    submitBusy,
  ]);

  if (!open) return null;

  const busy = isSubmitting || submitBusy;
  const canSubmitPitch = pitchHasPlayback;
  const canStartOrPauseRecord =
    !busy &&
    !pitchCameraError &&
    pitchCountdownNumber === null &&
    (pitchCameraReady || pitchHasPlayback || pitchRecording) &&
    !(pitchRecording && !SUPPORTS_RECORDER_PAUSE);

  const canStopRecord = pitchRecording && !busy && pitchCountdownNumber === null;

  const canDeleteRecording = pitchHasPlayback && !pitchRecording && !busy && pitchCountdownNumber === null;

  const recordButtonLabel = !pitchRecording
    ? "Start"
    : pitchRecordingPaused
      ? "Resume"
      : "Pause";
  const recordingStatusLabel = pitchRecording
    ? pitchRecordingPaused
      ? "Paused"
      : "Recording"
    : "Not recording";
  const recordingStatusClass = pitchRecording
    ? pitchRecordingPaused
      ? " pitch-recording-status-badge--paused"
      : " pitch-recording-status-badge--recording"
    : " pitch-recording-status-badge--idle";

  return (
    <>
      <div className="apply-help-overlay apply-shine-overlay--cancel-only" role="presentation">
        <div
          className="apply-help-modal apply-shine-modal job-create-pitch-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(event) => event.stopPropagation()}
        >
          <h2 id={titleId} className="apply-help-title apply-shine-title">
            {title}
          </h2>
          {introParagraphs.map((text, i) => (
            <p key={i} className="apply-shine-intro">
              {text}
            </p>
          ))}
          <p className="apply-shine-limit">{limitLine}</p>
          <div className="apply-shine-preview-wrap">
            <div className="apply-shine-preview-stage">
              <div
                className={`pitch-recording-status-badge${recordingStatusClass}`}
                role="status"
                aria-live="polite"
              >
                {recordingStatusLabel}
              </div>
              {pitchCountdownNumber !== null ? (
                <div className="apply-shine-countdown-overlay" aria-live="assertive">
                  <span className="apply-shine-countdown-num">{pitchCountdownNumber}</span>
                </div>
              ) : null}
              {pitchRecording && pitchRecordingRemainingSec !== null ? (
                <div className="apply-shine-rec-timer" aria-live="polite">
                  <span className="apply-shine-rec-timer-value">
                    {formatPitchTime(pitchRecordingRemainingSec)}
                  </span>
                  <span className="apply-shine-rec-timer-label">remaining</span>
                </div>
              ) : null}
              {pitchHasPlayback ? (
                <PitchVideoPlayer
                  key={pitchModalPlayerSrc || "pitch-preview"}
                  className="job-create-modal-pitch-player"
                  src={pitchModalPlayerSrc}
                  ariaLabel={pitchVideoAriaLabel}
                  knownDurationSec={pitchRecordedDurationSec}
                />
              ) : (
                <video ref={pitchVideoRef} className="apply-shine-preview" playsInline muted controls={false} />
              )}
            </div>
          </div>
          {pitchCameraError ? <p className="apply-shine-error">{pitchCameraError}</p> : null}
          {submitError ? <p className="apply-shine-error">{submitError}</p> : null}
          <div className="apply-shine-controls">
            <div className="apply-shine-recorder-actions" aria-label="Recording controls">
              <button
                type="button"
                className={`apply-shine-square-btn apply-shine-record-btn${pitchRecording ? " is-recording" : ""}${
                  pitchRecordingPaused ? " is-paused" : ""
                }`}
                onClick={handlePitchToggleRecord}
                disabled={!canStartOrPauseRecord}
              >
                <span className="apply-shine-square-btn-icon" aria-hidden="true">
                  {pitchRecording && !pitchRecordingPaused ? (
                    <svg
                      className="apply-shine-record-icon apply-shine-record-icon--pause"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                  ) : (
                    <span className="apply-shine-record-dot" />
                  )}
                </span>
                <span className="apply-shine-square-btn-label">{recordButtonLabel}</span>
              </button>
              <button
                type="button"
                className="apply-shine-square-btn apply-shine-stop-btn"
                onClick={handlePitchStopRecord}
                disabled={!canStopRecord}
              >
                <span className="apply-shine-square-btn-icon" aria-hidden="true">
                  <svg
                    className="apply-shine-stop-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                </span>
                <span className="apply-shine-square-btn-label">Stop</span>
              </button>
              <button
                type="button"
                className="apply-shine-square-btn job-create-pitch-delete-btn"
                onClick={handleDeletePitchModalRecording}
                disabled={!canDeleteRecording}
                aria-label="Delete recording"
                title="Delete recording"
              >
                <span className="apply-shine-square-btn-icon" aria-hidden="true">
                  <svg
                    className="icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="m18 7-.886 10.342c-.111 1.29-.166 1.936-.453 2.424a2.5 2.5 0 0 1-1.078.99c-.511.244-1.16.244-2.455.244h-2.256c-1.296 0-1.944 0-2.455-.244a2.5 2.5 0 0 1-1.078-.99c-.287-.488-.342-1.134-.453-2.424L6 7m-1.5-.5h4.615m0 0 .386-2.672c.112-.486.516-.828.98-.828h3.038c.464 0 .867.342.98.828l.386 2.672m-5.77 0h5.77m0 0H19.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="apply-shine-square-btn-label">Delete</span>
              </button>
            </div>
            <div className="apply-shine-primary-actions">
              <button
                type="button"
                className={submitButtonClassName}
                onClick={() => void handlePrimarySubmit()}
                disabled={!canSubmitPitch || busy || Boolean(pitchCameraError) || pitchCountdownNumber !== null}
              >
                {busy ? submittingLabel : submitLabel}
              </button>
              <button
                type="button"
                className="job-create-publish-back-btn apply-shine-cancel-btn"
                onClick={attemptClose}
              >
                {cancelLabel}
              </button>
            </div>
          </div>
          {showMobilePitchLink || applyHelpVideoSrc ? (
            <div className="apply-shine-info-links">
              {applyHelpVideoSrc ? (
                <button
                  type="button"
                  className="apply-shine-how-apply-link"
                  onClick={(event) => {
                    event.stopPropagation();
                    setShowApplyHelpInfo(true);
                  }}
                >
                  <svg className="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 16v-5h-.5m0 5h1M12 8.5V8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  How &quot;apply&quot; works?
                </button>
              ) : null}
              {showMobilePitchLink ? (
                <button
                  type="button"
                  className="job-create-pitch-info-link apply-shine-mobile-info-link"
                  onClick={(event) => {
                    event.stopPropagation();
                    setShowMobilePitchInfo(true);
                  }}
                >
                  <svg className="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 16v-5h-.5m0 5h1M12 8.5V8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Use your mobile?
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {applyHelpVideoSrc && showApplyHelpInfo ? (
        <div className="apply-help-overlay" role="presentation" onClick={() => setShowApplyHelpInfo(false)}>
          <div
            className="apply-help-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pitch-record-apply-help-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="apply-help-close"
              onClick={() => setShowApplyHelpInfo(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h2 id="pitch-record-apply-help-title" className="apply-help-title">
              How &quot;Apply&quot; works.
            </h2>
            <video className="apply-help-video" controls playsInline preload="metadata">
              <source src={applyHelpVideoSrc} type="video/mp4" />
              Your browser does not support embedded video.
            </video>
            <div className="apply-help-body">
              <p>
                Because we only want genuine people like you to apply for jobs on our platform and ensure no fake
                people are able to apply, we do things differently.
              </p>
              <p>
                When you press &quot;Apply&quot; you have the opportunity to explain to the hiring manager or the
                recruiter exactly why you feel you are suitable for the job. This needs to be a short video from your
                computer or mobile. Once you do this, we will share your profile along with the pitch. This makes sure
                only genuine people will apply.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {showDiscardPreviewConfirm ? (
        <div
          className="apply-help-overlay apply-shine-discard-overlay"
          role="presentation"
          onClick={() => setShowDiscardPreviewConfirm(false)}
        >
          <div
            className="apply-help-modal job-create-publish-confirm-modal apply-shine-discard-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pitch-record-discard-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="pitch-record-discard-title" className="apply-help-title">
              Close without saving?
            </h2>
            <p className="subtitle">{discardUnsavedPreviewMessage}</p>
            <div className="job-create-cancel-actions job-create-publish-confirm-actions job-create-info-actions">
              <button
                type="button"
                className="job-create-publish-back-btn"
                onClick={() => setShowDiscardPreviewConfirm(false)}
              >
                Keep editing
              </button>
              <button
                type="button"
                className="large-filled-button"
                onClick={() => {
                  setShowDiscardPreviewConfirm(false);
                  finalizeClose();
                }}
              >
                Close anyway
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showMobilePitchLink && showMobilePitchInfo ? (
        <div className="apply-help-overlay" role="presentation" onClick={() => setShowMobilePitchInfo(false)}>
          <div
            className="apply-help-modal job-create-publish-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pitch-record-mobile-info-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="pitch-record-mobile-info-title" className="apply-help-title">
              Using my mobile to record my application pitch
            </h3>
            <p className="subtitle">
              You can sign in to JOBUWIN on your mobile, open this job again, and press Apply to record your pitch with
              your phone&apos;s camera. This is useful if your computer does not have a camera or microphone.
            </p>
            <div className="job-create-cancel-actions job-create-publish-confirm-actions job-create-info-actions">
              <button
                type="button"
                className="job-create-publish-back-btn"
                onClick={() => setShowMobilePitchInfo(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
